import React from "react";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import { createPortal } from "react-dom";
import { FiBox } from "react-icons/fi";
import { IoClose } from "react-icons/io5";
import {
  getUserModel3DTitle,
  getUserModel3DUploaderLabel,
  UserModel3DLibraryItem,
} from "../../../features/floorplan3d/userModels/userModel3DTypes";
import { resolveUserModel3DUrl } from "../../../features/floorplan3d/userModels/userModel3DUrls";
import { floorplannerStore } from "../../../store/floorplannerStore";
import { useCurrencyRates } from "../../../hooks/useCurrencyRates";
import { formatAmountInDisplayCurrency } from "../../../utils/currency";

const dimension = (
  value: number | null | undefined,
  unavailableLabel: string,
) =>
  typeof value === "number" && Number.isFinite(value)
    ? `${value.toFixed(2)} m`
    : unavailableLabel;

const date = (value: string | null | undefined, unavailableLabel: string) =>
  value
    ? new Intl.DateTimeFormat(undefined, { dateStyle: "medium" }).format(
        new Date(value),
      )
    : unavailableLabel;

export const CommunityModelDetailModal: React.FC<{
  model: UserModel3DLibraryItem;
  onClose: () => void;
}> = observer(({ model, onClose }) => {
  const { t, i18n } = useTranslation();
  const { ratesArray, base } = useCurrencyRates();
  const thumbnail = resolveUserModel3DUrl(model.thumbnailUrl);
  const modelTitle = getUserModel3DTitle(model);
  const uploaderLabel = getUserModel3DUploaderLabel(model);
  const brand = model.brand?.trim();
  const formattedPrice = formatAmountInDisplayCurrency(
    model.price,
    model.currency || "SEK",
    floorplannerStore.currency,
    ratesArray,
    base,
    i18n.language,
  );
  React.useEffect(() => {
    const handleKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      event.stopPropagation();
      onClose();
    };
    document.addEventListener("keydown", handleKey, true);
    return () => document.removeEventListener("keydown", handleKey, true);
  }, [onClose]);

  return createPortal(
    <div
      data-product-library-modal="true"
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/40 p-3 sm:p-6"
      onMouseDown={(event) => {
        event.stopPropagation();
      }}
      onClick={(event) => {
        event.stopPropagation();
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={modelTitle}
        onMouseDown={(event) => event.stopPropagation()}
        className="relative grid max-h-[calc(100vh-32px)] w-full max-w-4xl overflow-hidden rounded-2xl bg-[#F8F7F5] shadow-2xl md:grid-cols-2"
      >
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            onClose();
          }}
          aria-label={t("productLibrary.closeObjectDetails")}
          className="absolute right-3 top-3 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-white text-[#111] shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-[#111]"
        >
          <IoClose size={20} />
        </button>
        <section className="flex min-h-[280px] items-center justify-center bg-white p-6">
          {thumbnail ? (
            <img
              src={thumbnail}
              alt=""
              crossOrigin="use-credentials"
              className="h-full max-h-[520px] w-full object-contain"
            />
          ) : (
            <FiBox size={52} className="text-gray-400" />
          )}
        </section>
        <section className="overflow-y-auto p-6 sm:p-8">
          <h2 className="pr-10 text-2xl font-semibold tracking-[-0.02em] text-[#111]">
            {modelTitle}
          </h2>
          {model.displayDescription && (
            <p className="mt-3 text-sm leading-relaxed text-[#3A3A3A]">
              {model.displayDescription}
            </p>
          )}
          <dl className="mt-6 grid grid-cols-[auto,1fr] gap-x-4 gap-y-3 border-t border-black/10 pt-5 text-sm">
            <dt className="text-gray-500">{t("productLibrary.uploadedBy")}</dt>
            <dd className="break-all text-[#111]">{uploaderLabel}</dd>
            {brand ? (
              <>
                <dt className="text-gray-500">{t("userModels3d.brand")}</dt>
                <dd className="break-all text-[#111]">{brand}</dd>
              </>
            ) : null}
            {formattedPrice ? (
              <>
                <dt className="text-gray-500">{t("productLibrary.price")}</dt>
                <dd className="text-[#111]">
                  {formattedPrice}{" "}
                  <span className="text-xs text-gray-500">
                    {t("common.inclVat")}
                  </span>
                </dd>
              </>
            ) : null}
            <dt className="text-gray-500">{t("productLibrary.filename")}</dt>
            <dd className="break-all text-[#111]">{model.originalFilename}</dd>
            <dt className="text-gray-500">{t("productLibrary.dimensions")}</dt>
            <dd className="text-[#111]">
              {t("common.widthAbbr")}{" "}
              {dimension(model.dimensions?.width, t("common.unavailable"))} ·{" "}
              {t("common.depthAbbr")}{" "}
              {dimension(model.dimensions?.depth, t("common.unavailable"))} ·{" "}
              {t("common.heightAbbr")}{" "}
              {dimension(model.dimensions?.height, t("common.unavailable"))}
            </dd>
            <dt className="text-gray-500">{t("productLibrary.uploaded")}</dt>
            <dd className="text-[#111]">{date(model.createdAt, t("common.unavailable"))}</dd>
          </dl>
        </section>
      </div>
    </div>,
    document.body,
  );
});
